import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import authService from '../services/auth';
import {
  ensureAccountLinkCaptureReady,
  hasPendingAccountLinkSession,
  startAccountLink,
} from '../utils/accountLinking';
import { detectPlatformAsync } from '../platforms';
import { getDevProviderUserId, isDevAuthEnabled } from '../utils/devAuth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);

      const platform = await detectPlatformAsync();
      await ensureAccountLinkCaptureReady(platform);

      if (hasPendingAccountLinkSession()) {
        if (authService.checkAuth()) {
          setIsAuthenticated(true);
          try {
            const userInfo = await authService.fetchUserInfo();
            setUser(userInfo);
          } catch (err) {
            console.warn('[Auth] Could not preload user while link pending:', err);
          }
        }
        setIsLoading(false);
        return;
      }

      if (authService.checkAuth()) {
        const isValid = await authService.ensureValidToken();
        if (isValid) {
          try {
            const userInfo = await authService.fetchUserInfo();
            setUser(userInfo);
            setIsAuthenticated(true);
          } catch (err) {
            authService.clearAuth();
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Auth initialization error:', err);
      setError(err.message);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const authenticateDev = useCallback(async (providerUserId = getDevProviderUserId()) => {
    if (!isDevAuthEnabled() || !providerUserId) {
      return { success: false, error: 'Dev auth is not configured' };
    }

    try {
      setIsLoading(true);
      setError(null);

      await authService.authenticateWithDebugLogin(providerUserId);
      const userInfo = await authService.fetchUserInfo();

      setUser(userInfo);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Debug authentication failed';
      setError(errorMessage);
      setIsAuthenticated(false);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const authenticate = useCallback(async (provider, payload) => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.authenticateWithProvider(provider, payload);
      const userInfo = await authService.fetchUserInfo();

      setUser(userInfo);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Authentication failed';
      setError(errorMessage);
      setIsAuthenticated(false);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const applyLinkedUser = useCallback((linkResult) => {
    if (!linkResult?.user) {
      return;
    }
    setUser({ type: 'user', user: linkResult.user });
    setIsAuthenticated(true);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userInfo = await authService.fetchUserInfo();
      setUser(userInfo);
      setIsAuthenticated(true);
      return userInfo;
    } catch (err) {
      console.error('Refresh user error:', err);
      throw err;
    }
  }, []);

  const linkPlatform = useCallback(async (targetProvider) => {
    const token = authService.getToken();
    if (!token) {
      throw new Error('Требуется авторизация');
    }
    await startAccountLink(targetProvider, token, authService);
  }, []);

  const value = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    error,
    isDevAuth: isDevAuthEnabled(),
    authenticate,
    authenticateDev,
    logout,
    refreshUser,
    applyLinkedUser,
    linkPlatform,
  }), [
    isAuthenticated,
    isLoading,
    user,
    error,
    authenticate,
    authenticateDev,
    logout,
    refreshUser,
    applyLinkedUser,
    linkPlatform,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
